import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ArrowDownToLine,
  CheckCircle2,
  Circle,
  Copy,
  Loader2,
  PackageCheck,
  RotateCcw,
  Undo2,
  XCircle,
  AlertTriangle,
  Check,
} from "lucide-react";
import { canUpdateCases, useCaseSoftware } from "hooks/useCaseSoftware";
import {
  compareRelease,
  fmtBytes,
  isInterrupted,
  outcomeOf,
  stepOf,
  UPDATE_STEPS,
  updateBlockedReason,
  updateReport,
} from "utils/caseSoftware";
import { caseVersion } from "utils/caseDashboard";
import { ReleaseNotes } from "../UpdateFirmwareFlow";
import {
  Card,
  EmptyState,
  Notice,
  PrimaryButton,
  SecondaryButton,
  fmtDateTime,
} from "./parts";

const CHANNELS = ["production", "dev"];

const RESULT_STYLE = {
  updated: "bg-[#ECFDF5] text-[#047857]",
  rolled_back: "bg-[#FEF3E7] text-[#9A4A17]",
  failed: "bg-[#FEF2F2] text-[#B91C1C]",
  refused: "bg-[#F3F4F6] text-[#6B7280]",
};

const isoTime = (value) => (value ? fmtDateTime(value) : "—");

/** Software tab: the case's version, updates (admins), progress, history. */
const SoftwareView = ({ caseId, caseRecord, status, health }) => {
  const { t } = useTranslation();
  const isAdmin = canUpdateCases();
  const sw = useCaseSoftware(caseId, { enabled: true, isAdmin });
  const versions = sw.versions.data;
  const updateState = sw.status.data;
  const blocked = updateBlockedReason({
    health: health.data,
    versions,
    updateState: sw.sending ? { phase: "uploading" } : updateState,
  });

  // A run just finished: the app restarted, so refresh what it reports.
  const finished = outcomeOf(updateState) ? updateState.id : null;
  const { refresh: refreshStatus } = status;
  const { refresh: refreshHealth } = health;
  useEffect(() => {
    if (finished) {
      refreshStatus?.();
      refreshHealth?.();
    }
  }, [finished, refreshStatus, refreshHealth]);

  const running = caseVersion(status.data);
  const current = {
    label: versions?.current || null,
    version: running?.display || null,
    baseVersion: running?.base || null,
  };

  return (
    <div className="space-y-5">
      <InstalledCard
        versions={versions}
        running={running}
        isAdmin={isAdmin}
        blocked={blocked}
        onRollback={sw.rollback}
      />

      {sw.startError && (
        <Notice tone="error">
          {t("caseDashboard.software.startFailed", { message: sw.startError })}
        </Notice>
      )}

      {(sw.updating || sw.log.lines.length > 0 || outcomeOf(updateState)) && (
        <ProgressCard
          caseRecord={caseRecord}
          sw={sw}
          updateState={updateState}
        />
      )}

      {isInterrupted(updateState) && (
        <Notice tone="warn">{t("caseDashboard.software.interrupted")}</Notice>
      )}

      {isAdmin ? (
        <UpdateCard sw={sw} current={current} blocked={blocked} />
      ) : (
        <Notice>{t("caseDashboard.software.adminsOnly")}</Notice>
      )}

      <HistoryCard history={sw.history} />
    </div>
  );
};

// ── This case ────────────────────────────────────────────────────────────────

const InstalledCard = ({ versions, running, isAdmin, blocked, onRollback }) => {
  const { t } = useTranslation();
  const [confirming, setConfirming] = useState(false);
  const installed = versions?.installed || [];
  const label = (l) =>
    installed.find((r) => r.label === l)?.version || l || "—";
  const canRollBack =
    isAdmin && versions?.previous && versions.previous !== versions.current;

  return (
    <Card title={t("caseDashboard.software.thisCase")}>
      <div className="flex flex-wrap items-center gap-6 p-4">
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#FEF3E7]">
          <PackageCheck size={24} className="text-[#E07A3A]" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-medium uppercase tracking-wide text-[#9CA3AF]">
            {t("caseDashboard.software.running")}
          </p>
          <p className="truncate text-lg font-bold text-[#111827]">
            {running?.display || label(versions?.current)}
          </p>
          {versions?.previous && (
            <p className="text-[12px] text-[#6B7280]">
              {t("caseDashboard.software.previous", {
                version: label(versions.previous),
              })}
            </p>
          )}
        </div>
        {canRollBack &&
          (confirming ? (
            <div className="flex items-center gap-2">
              <span className="text-[13px] text-[#374151]">
                {t("caseDashboard.software.rollbackConfirm", {
                  version: label(versions.previous),
                })}
              </span>
              <SecondaryButton onClick={() => setConfirming(false)}>
                {t("caseDashboard.common.cancel")}
              </SecondaryButton>
              <PrimaryButton
                onClick={() => {
                  setConfirming(false);
                  onRollback();
                }}
              >
                <Undo2 size={15} />
                {t("caseDashboard.software.rollback")}
              </PrimaryButton>
            </div>
          ) : (
            <SecondaryButton
              disabled={!!blocked}
              title={
                blocked ? t(`caseDashboard.software.blocked.${blocked}`) : ""
              }
              onClick={() => setConfirming(true)}
            >
              <Undo2 size={15} />
              {t("caseDashboard.software.rollbackTo", {
                version: label(versions.previous),
              })}
            </SecondaryButton>
          ))}
      </div>
      {versions?.layout === "checkout" && (
        <div className="border-t border-[#F3F4F6] p-4">
          <Notice>{t("caseDashboard.software.checkout")}</Notice>
        </div>
      )}
      {versions?.layout === "releases" && versions.signing_key === false && (
        <div className="border-t border-[#F3F4F6] p-4">
          <Notice tone="warn">
            {t("caseDashboard.software.noSigningKey")}
          </Notice>
        </div>
      )}
    </Card>
  );
};

// ── Update (admins) ──────────────────────────────────────────────────────────

const UpdateCard = ({ sw, current, blocked }) => {
  const { t } = useTranslation();
  const { latest, downloads } = sw;
  const release = latest.data;
  const relation = compareRelease(release, current);
  const downloaded = (label) =>
    downloads.releases.find((r) => r.label === label);
  // Downloaded releases besides the latest: installable with no internet.
  const others = downloads.releases.filter((r) => r.label !== release?.tag);

  return (
    <Card
      title={t("caseDashboard.software.updates")}
      action={
        <div className="flex rounded-lg bg-[#F3F4F6] p-0.5">
          {CHANNELS.map((c) => (
            <button
              type="button"
              key={c}
              onClick={() => sw.setChannel(c)}
              className={`rounded-md px-3 py-1 text-[12px] font-medium transition duration-150 ${
                sw.channel === c
                  ? "bg-white text-[#111827] shadow-sm"
                  : "text-[#6B7280] hover:text-[#374151]"
              }`}
            >
              {t(`caseDashboard.software.channel.${c}`)}
            </button>
          ))}
        </div>
      }
    >
      <div className="space-y-4 p-4">
        {latest.loading && !release ? (
          <p className="flex items-center gap-2 text-[13px] text-[#6B7280]">
            <Loader2 size={14} className="animate-spin" />
            {t("caseDashboard.software.checking")}
          </p>
        ) : latest.error ? (
          <Notice tone="warn">
            {t("caseDashboard.software.latestError", {
              message: latest.error,
            })}
          </Notice>
        ) : !release ? (
          <p className="text-[13px] text-[#6B7280]">
            {t("caseDashboard.software.noRelease")}
          </p>
        ) : (
          <ReleaseRow
            release={{
              label: release.tag,
              version: release.version,
              releasedAt: release.released_at,
              size: release.artifacts?.bundle?.size,
              notes: release.notes,
            }}
            relation={relation}
            downloaded={downloaded(release.tag)}
            progress={downloads.progress[release.tag]}
            error={downloads.errors[release.tag]}
            onDownload={() => downloads.download(release)}
            onInstall={() => sw.startUpdate(release.tag)}
            blocked={blocked}
            busy={sw.updating}
          />
        )}

        {others.length > 0 && (
          <div className="border-t border-[#F3F4F6] pt-4">
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-[#9CA3AF]">
              {t("caseDashboard.software.onThisComputer")}
            </p>
            <div className="space-y-3">
              {others.map((r) => (
                <ReleaseRow
                  key={r.label}
                  compact
                  release={r}
                  relation={compareRelease(
                    {
                      tag: r.label,
                      version: r.version,
                      base_version: r.baseVersion,
                    },
                    current
                  )}
                  downloaded={r}
                  onInstall={() => sw.startUpdate(r.label)}
                  blocked={blocked}
                  busy={sw.updating}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};

const RELATION_STYLE = {
  same: "bg-[#ECFDF5] text-[#047857]",
  newer: "bg-[#FEF3E7] text-[#C2611F]",
  older: "bg-[#F3F4F6] text-[#6B7280]",
  different: "bg-[#EDE9FE] text-[#6D28D9]",
};

const ReleaseRow = ({
  release,
  relation,
  downloaded,
  progress,
  error,
  onDownload,
  onInstall,
  blocked,
  busy,
  compact,
}) => {
  const { t } = useTranslation();
  const pct =
    progress?.total > 0
      ? Math.min(100, Math.round((progress.received / progress.total) * 100))
      : null;
  const installed = relation === "same";
  const reason = blocked ? t(`caseDashboard.software.blocked.${blocked}`) : "";

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-0 flex-1">
          <p className="flex items-center gap-2 text-[15px] font-semibold text-[#111827]">
            {release.version}
            {RELATION_STYLE[relation] && (
              <span
                className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${RELATION_STYLE[relation]}`}
              >
                {t(`caseDashboard.software.relation.${relation}`)}
              </span>
            )}
          </p>
          <p className="text-[12px] text-[#6B7280]">
            {[
              release.releasedAt &&
                t("caseDashboard.software.released", {
                  time: isoTime(release.releasedAt),
                }),
              release.size && fmtBytes(release.size),
            ]
              .filter(Boolean)
              .join(" · ")}
          </p>
        </div>

        {!downloaded && !progress && onDownload && (
          <SecondaryButton onClick={onDownload}>
            <ArrowDownToLine size={15} />
            {t("caseDashboard.software.download")}
          </SecondaryButton>
        )}
        {/* Going back to an older build is allowed, but not the suggested move. */}
        {downloaded && !installed && relation === "older" && (
          <SecondaryButton
            onClick={onInstall}
            disabled={!!blocked || busy}
            title={reason}
          >
            {t("caseDashboard.software.downgrade", {
              version: release.version,
            })}
          </SecondaryButton>
        )}
        {downloaded && !installed && relation !== "older" && (
          <PrimaryButton
            onClick={onInstall}
            disabled={!!blocked || busy}
            title={reason}
          >
            {t("caseDashboard.software.install", { version: release.version })}
          </PrimaryButton>
        )}
      </div>

      {progress && (
        <div>
          <div className="h-1.5 overflow-hidden rounded-full bg-[#F3F4F6]">
            <div
              className="h-full rounded-full bg-[#E07A3A] transition-all duration-300"
              style={{ width: `${pct ?? 5}%` }}
            />
          </div>
          <p className="mt-1 text-[11px] text-[#6B7280]">
            {t("caseDashboard.software.downloading", {
              done: fmtBytes(progress.received),
              total: fmtBytes(progress.total),
            })}
          </p>
        </div>
      )}
      {error && <Notice tone="error">{error}</Notice>}
      {downloaded && !installed && !compact && (
        <p className="flex items-center gap-1.5 text-[12px] text-[#047857]">
          <Check size={13} />
          {t("caseDashboard.software.ready")}
        </p>
      )}
      {downloaded && !installed && blocked && (
        <p className="text-[12px] text-[#9A4A17]">{reason}</p>
      )}
      {!compact && release.notes && (
        <ReleaseNotes
          notes={release.notes}
          title={t("caseDashboard.software.whatsNew")}
        />
      )}
    </div>
  );
};

// ── Progress and outcome ─────────────────────────────────────────────────────

const ProgressCard = ({ caseRecord, sw, updateState }) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const outcome = sw.updating ? null : outcomeOf(updateState);
  const phase = sw.sending ? "uploading" : updateState?.phase;
  const rollingBack =
    phase === "rolling_back" || updateState?.action === "rollback";
  const step = stepOf(phase);
  const sendPct =
    sw.sending?.total > 0
      ? Math.round((sw.sending.sent / sw.sending.total) * 100)
      : null;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(
        updateReport({
          caseName: caseRecord?.name,
          state: updateState,
          lines: sw.log.lines,
        })
      );
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard unavailable
    }
  };

  return (
    <Card
      title={
        outcome
          ? t(`caseDashboard.software.outcome.${outcome.kind}.title`, {
              version: outcome.version,
              from: outcome.from,
            })
          : rollingBack
          ? t("caseDashboard.software.rollingBack")
          : t("caseDashboard.software.updatingTo", {
              version: updateState?.version || sw.sending?.label || "",
            })
      }
      action={
        outcome && (
          <SecondaryButton onClick={copy} className="!px-3 !py-1 text-[12px]">
            <Copy size={13} />
            {copied
              ? t("caseDashboard.software.copied")
              : t("caseDashboard.software.copyDetails")}
          </SecondaryButton>
        )
      }
    >
      <div className="space-y-4 p-4">
        {outcome ? (
          <OutcomeSummary outcome={outcome} />
        ) : (
          !rollingBack && (
            <ol className="grid grid-cols-6 gap-2">
              {UPDATE_STEPS.map((name, i) => {
                const done = step != null && i < step;
                const active = i === step;
                return (
                  <li key={name} className="min-w-0">
                    <div
                      className={`mb-1.5 h-1 rounded-full ${
                        done
                          ? "bg-[#10B981]"
                          : active
                          ? "bg-[#E07A3A]"
                          : "bg-[#F3F4F6]"
                      }`}
                    />
                    <p
                      className={`flex items-center gap-1 truncate text-[12px] ${
                        active
                          ? "font-semibold text-[#111827]"
                          : done
                          ? "text-[#047857]"
                          : "text-[#9CA3AF]"
                      }`}
                    >
                      {done ? (
                        <CheckCircle2 size={12} />
                      ) : active ? (
                        <Loader2 size={12} className="animate-spin" />
                      ) : (
                        <Circle size={12} />
                      )}
                      {t(`caseDashboard.software.steps.${name}`)}
                    </p>
                  </li>
                );
              })}
            </ol>
          )
        )}

        {!outcome && phase === "uploading" && (
          <p className="text-[12px] text-[#6B7280]">
            {t("caseDashboard.software.sending", {
              done: fmtBytes(sw.sending?.sent),
              total: fmtBytes(sw.sending?.total),
              pct: sendPct ?? 0,
            })}
          </p>
        )}
        {!outcome && phase === "retrying" && (
          <Notice tone="warn">
            {t("caseDashboard.software.retrying", {
              reason: updateState?.first_failure,
            })}
          </Notice>
        )}
        {!outcome && phase === "rolling_back" && (
          <Notice tone="warn">
            {t("caseDashboard.software.rollingBackBecause", {
              reason: updateState?.failure,
            })}
          </Notice>
        )}
        {!outcome && (
          <p className="text-[12px] text-[#6B7280]">
            {t("caseDashboard.software.keepsGoing")}
          </p>
        )}

        {sw.log.lines.length > 0 ? (
          <pre className="max-h-64 overflow-auto rounded-lg bg-[#111827] p-3 font-mono text-[11px] leading-relaxed text-[#E5E7EB]">
            {sw.log.lines.slice(outcome ? -50 : -200).join("\n")}
          </pre>
        ) : (
          outcome && (
            <button
              type="button"
              onClick={() => sw.log.start("update-log")}
              className="text-[12px] font-medium text-[#E07A3A] hover:text-[#D66B2E]"
            >
              {t("caseDashboard.software.showLog")}
            </button>
          )
        )}
      </div>
    </Card>
  );
};

const OUTCOME_ICON = {
  updated: { icon: CheckCircle2, color: "#10B981" },
  rolled_back: { icon: RotateCcw, color: "#E07A3A" },
  failed: { icon: XCircle, color: "#DC2626" },
  refused: { icon: AlertTriangle, color: "#6B7280" },
};

const OutcomeSummary = ({ outcome }) => {
  const { t } = useTranslation();
  const look = OUTCOME_ICON[outcome.kind] || OUTCOME_ICON.refused;
  const Icon = look.icon;
  return (
    <div className="flex items-start gap-3">
      <Icon size={22} style={{ color: look.color }} className="shrink-0" />
      <div className="min-w-0 text-[13px] text-[#374151]">
        <p>
          {t(`caseDashboard.software.outcome.${outcome.kind}.body`, {
            version: outcome.version,
            from: outcome.from,
          })}
        </p>
        {outcome.error && (
          <p className="mt-1 text-[#6B7280]">
            {t("caseDashboard.software.reason", { reason: outcome.error })}
          </p>
        )}
        {outcome.finishedAt && (
          <p className="mt-1 text-[11px] text-[#9CA3AF]">
            {isoTime(outcome.finishedAt)}
          </p>
        )}
      </div>
    </div>
  );
};

// ── History ──────────────────────────────────────────────────────────────────

const HistoryCard = ({ history }) => {
  const { t } = useTranslation();
  const rows = Array.isArray(history.data) ? history.data : [];
  return (
    <Card title={t("caseDashboard.software.history")}>
      {rows.length === 0 ? (
        <EmptyState
          icon={PackageCheck}
          title={t("caseDashboard.software.noHistory")}
        />
      ) : (
        <ul className="divide-y divide-[#F3F4F6]">
          {rows.map((r) => (
            <li key={r.id} className="flex items-center gap-4 px-4 py-3">
              <span className="w-36 shrink-0 text-[12px] text-[#6B7280]">
                {isoTime(r.finished_at || r.started_at)}
              </span>
              <span className="min-w-0 flex-1 truncate text-[13px] text-[#111827]">
                {t(`caseDashboard.software.historyRow.${r.action}`, {
                  from: r.from || "—",
                  to: r.version || r.target || "—",
                })}
                {r.error && (
                  <span className="ml-2 text-[12px] text-[#6B7280]">
                    {r.error}
                  </span>
                )}
              </span>
              <span
                className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold ${
                  RESULT_STYLE[r.result] || RESULT_STYLE.refused
                }`}
              >
                {t(`caseDashboard.software.result.${r.result || "refused"}`)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
};

export default SoftwareView;
